import React from 'react';
import { Handshake, Sparkles } from 'lucide-react';

import { NewLeadTile, RunTile } from './LeadTile';

// A brand-new lead (Nic 2026-10-23): its run is live, nobody has reached
// out yet (the same rule as the tile's New chip), and nobody is on a call
// with them right now. Day 1 lists these first, under their own New header;
// dialing them, or any logged call, text or email, moves them down into
// Day 1. Timing and colors are the cadence's, as before.
export const isNewRun = (run) => run?.state === 'active' && run?.lead?.leadStatus === 'new' && !run?.activeCall;

// Side by side from 1360px wide (Nic 2026-10-23: at lg, 1024px, ten columns
// squeezed the tiles until text spilled out); narrower, they stack.
// A day column. Fluid width — every full column takes an equal share of the
// board, so nothing ever scrolls horizontally. Quiet columns (Day 4 breather,
// Days 8-9) render as slim vertical rails by default (nothing is ever due
// there); click to expand/collapse.
const BoardColumn = ({
  column,
  items,
  // How many leads in this column have reached out. Passed in rather than
  // counted from `items` because the caller computes it BEFORE the Hide Done
  // filter — the marker must survive its own tile being hidden.
  reachedOut = 0,
  now,
  cadenceVersion,
  onOpenRun,
  slim,
  onToggleSlim,
  // (run) => the OWNER's effective lunch today ({startAt, endAt} absolute
  // instants) — tiles freeze/mark their clocks around it.
  ownerLunchFor = null,
  // The board search is narrowing the list: an empty column says so, so it
  // never reads as caught up (Nic 2026-10-21).
  filtered = false,
  // Day 1 only (Nic 2026-10-21, Intake folded into Day 1): brand-new leads
  // nobody has taken, oldest first, pinned above the cadence tiles. Never
  // touched by Hide done (they are not runs). Their clock stands still for
  // the owner's lunch, meetings and breaks (arrivalPausesFor).
  arrivals = [],
  onOpenArrival = null,
  arrivalPausesFor = null,
  // Day 1 only: brand-new leads get their own New section at the top, which
  // goes away when there are none (Nic 2026-10-23).
  newSection = false,
  // The tiles show whose lead it is (someone who sees everyone's).
  showOwners = false,
}) => {
  const newRuns = newSection ? items.filter(isNewRun) : [];
  const restRuns = newRuns.length ? items.filter((run) => !isNewRun(run)) : items;
  // Communal arrivals are new by definition (only Day 1 is handed any).
  const newCount = newRuns.length + arrivals.length;
  const count = items.length + arrivals.length;
  const renderRun = (run) => (
      <div key={run.cadenceRunId} data-run-tile>
        <RunTile
          run={run}
          now={now}
          onOpen={onOpenRun}
          cadenceVersion={cadenceVersion}
          ownerLunch={ownerLunchFor ? ownerLunchFor(run) : null}
      showOwner={showOwners}
        />
      </div>
  );
  // Shared by both markers below (the collapsed rail and the header), so the
  // same raised hand reads identically wherever the column happens to be.
  const reachOutTitle = `${reachedOut} lead${reachedOut > 1 ? 's' : ''} here ${
    reachedOut > 1 ? 'have' : 'has'
  } reached out`;

  if (slim) {
    return (
      <button
        type="button"
        onClick={onToggleSlim}
        title={`${column.title}: ${column.quiet || 'quiet'} · ${count} lead(s) parked · click to expand`}
        className="flex shrink-0 items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] transition-colors hover:bg-slate-50 min-[1360px]:h-full min-[1360px]:w-9 min-[1360px]:flex-col min-[1360px]:px-0 min-[1360px]:py-2.5 dark:!bg-surface-card dark:ring-1 dark:ring-white/10 dark:hover:bg-slate-700/50"
      >
        <span
          className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
            count > 0
              ? 'bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300'
              : 'bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-slate-500'
          }`}
        >
          {count}
        </span>
        {/* Reach-out marker BELOW the count: a bare emerald handshake with a
            slow breathe (no fill — premium, the motion is the signal). A
            parked lead raising their hand is exactly what a breather column
            must not hide (Nic 2026-08-07) — the same durable reach-out fact
            the tile chip keys off, so it survives dismissed banners. */}
        {reachedOut > 0 ? (
          <span
            title={reachOutTitle}
            className="os-reachout-breathe inline-flex items-center gap-0.5 text-emerald-500 dark:text-emerald-400"
          >
            <Handshake size={14} strokeWidth={2.4} />
            {reachedOut > 1 ? (
              <span className="text-[10px] font-bold">{reachedOut}</span>
            ) : null}
          </span>
        ) : null}
        {/* Vertical writing only in the desktop rail; stacked mode reads as a
            normal one-line row. */}
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 min-[1360px]:mt-2 min-[1360px]:[writing-mode:vertical-rl]">
          {column.title}
        </span>
      </button>
    );
  }

  return (
    // Ghost column: no card chrome — just a header over the canvas, tiles
    // float as shadowed cards (the modern borderless board language). The one
    // exception: an EXPANDED quiet column keeps a subtle outline so it reads
    // as the thing that can collapse back down.
    <div
      className={`flex min-w-0 shrink-0 flex-col rounded-xl min-[1360px]:h-full min-[1360px]:flex-1 min-[1360px]:shrink ${
        column.collapsible
          ? 'cursor-pointer ring-[1.5px] ring-inset ring-slate-300/80 dark:ring-white/15'
          : ''
      }`}
      // Anywhere in an expanded quiet column that isn't a lead card collapses
      // it — trekking back to the tiny title is bad UX (Nic 2026-08-06).
      // Tile clicks (open the slide-over) are excluded.
      onClick={
        column.collapsible
          ? (e) => {
              if (e.defaultPrevented) return;
              if (e.target.closest('[data-run-tile]')) return;
              onToggleSlim();
            }
          : undefined
      }
    >
      <div className="flex items-center justify-between px-2.5 pb-1 pt-2.5">
        {/* Only collapsible columns render a button — Bootstrap's reboot forces
            cursor:pointer on all buttons, so a plain heading must be a span. */}
        {column.collapsible ? (
          <button
            type="button"
            onClick={(e) => {
              // The column-background handler would fire too — one toggle.
              e.stopPropagation();
              onToggleSlim();
            }}
            className="min-w-0 cursor-pointer truncate text-left text-[12px] font-semibold uppercase tracking-wide text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
            title={`${column.quiet} · click to collapse`}
          >
            {column.title}
          </button>
        ) : (
          <span
            className="min-w-0 truncate text-[12px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
            title={column.title}
          >
            {column.title}
          </span>
        )}
        {/* Column-level reach-out marker (Nic 2026-08-20): the same breathing
            handshake the tiles wear, centered in the slack between the title
            and the count. With a tall column, Hide Done on, or the tile
            simply scrolled below the fold, the tile's own chip can't be seen
            — this says "there is a raised hand on this day" from the header,
            which is always in view. Counted before the Hide Done filter, so
            it does not vanish with the tile it points at. */}
        {reachedOut > 0 ? (
          <span
            title={`${reachOutTitle}. Find the handshake on a tile in this column.`}
            className="os-reachout-breathe mx-1 inline-flex flex-1 items-center justify-center gap-0.5 text-emerald-500 dark:text-emerald-400"
          >
            <Handshake size={14} strokeWidth={2.4} />
            {reachedOut > 1 ? (
              <span className="text-[10px] font-bold">{reachedOut}</span>
            ) : null}
          </span>
        ) : null}
        <span className="ml-1 shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-400">
          {count}
        </span>
      </div>
      <div className="os-scroll flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-2 pb-2.5 pt-1">
        {count === 0 ? (
          filtered ? (
            <div className="rounded-lg border-[1px] border-dashed border-sky-300/80 bg-sky-50 px-2 py-4 text-center text-xs italic text-sky-700 dark:border-sky-400/20 dark:!bg-sky-500/10 dark:text-sky-300">
              No matches for this search
            </div>
          ) : (
            <div className="rounded-lg border-[1px] border-dashed border-slate-300/80 px-2 py-4 text-center text-xs text-slate-400 dark:border-white/[0.08] dark:text-slate-500">
              Nobody here
            </div>
          )
        ) : null}
        {newCount > 0 ? (
          <div
            data-new-section
            className="space-y-2 rounded-xl bg-indigo-50/80 p-1.5 ring-2 ring-indigo-300 dark:!bg-indigo-500/10 dark:ring-indigo-400/50"
          >
            <div className="flex items-center gap-1.5 px-1 pt-0.5">
              <Sparkles size={13} className="text-indigo-600 dark:text-indigo-300" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-200">
                New
              </span>
              <span className="ml-auto rounded-full bg-indigo-600 px-1.5 text-[10.5px] font-bold tabular-nums text-white">
                {newCount}
              </span>
            </div>
        {arrivals.map((lead) => (
          <div key={`new-${lead.leadId}`} data-run-tile>
            <NewLeadTile
              lead={lead}
              now={now}
              onOpen={onOpenArrival}
              pauses={arrivalPausesFor ? arrivalPausesFor(lead) : null}
            />
          </div>
        ))}
        {newRuns.map(renderRun)}
          </div>
        ) : null}
        {restRuns.map(renderRun)}
      </div>
    </div>
  );
};

export default BoardColumn;
